"use client";

import React, { useState, useEffect } from 'react';
import { Block } from '../../interfaces.ts';
import TemplateSettingsDialog from './TemplateSettingsDialog.tsx';
import LocaleFieldWrapper from '../../components/LocaleFieldWrapper.tsx';
import {useLocale} from '../../context/localeContext.tsx';

interface MediaBlockTemplateSettingsDialogProps {
  block: Block;
  onClose: () => void;
  onSave: (updatedBlock: Block) => void;
  availableLocales: string[];
  fallbackLocale?: string;
  newMediaCallback?: (args: { blockId: string }) => Promise<string>;
  mediaLibraryCallback?: (args: { blockId: string }) => Promise<string>;
}

const MediaBlockTemplateSettingsDialog: React.FC<MediaBlockTemplateSettingsDialogProps> = ({
  block,
  onClose,
  onSave,
  availableLocales,
  newMediaCallback,
  mediaLibraryCallback
}) => {
  const { activeLocale } = useLocale();

  // Local state for content and properties (not part of context)
  const [localeContent, setLocaleContent] = useState<{
    [key: string]: { src: string; altText: string; type?: 'image' | 'video' | 'gallery'; gallerySrc?: string[] };
  }>(
    availableLocales.reduce((acc: any, locale: string) => {
      acc[locale] = {
        src: block.defaultContent?.[locale]?.src || '',
        altText: block.defaultContent?.[locale]?.altText || '',
        type: block.defaultContent?.[locale]?.type || 'image',
        gallerySrc: block.defaultContent?.[locale]?.gallerySrc || [],
      };
      return acc;
    }, {})
  );

  useEffect(() => {
    const currentContent = localeContent[activeLocale];
    if (!currentContent) {
      setLocaleContent((prev) => ({
        ...prev,
        [activeLocale]: { src: '', altText: '', type: 'image', gallerySrc: [] },
      }));
    }
  }, [activeLocale, localeContent]);

  const handleSave = () => {
    onSave({
      ...block,
      properties: {
        ...block.properties,
      },
      defaultContent: localeContent,
    });
  };

  const handleSrcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocaleContent((prev) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], src: e.target.value },
    }));
  };

  const handleAltTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocaleContent((prev) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], altText: e.target.value },
    }));
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocaleContent((prev) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], type: e.target.value as 'image' | 'video' | 'gallery' },
    }));
  };

  const handleNewMediaClick = async () => {
    if (newMediaCallback) {
      try {
        const url = await newMediaCallback({ blockId: block.id });
        setLocaleContent((prev) => ({
          ...prev,
          [activeLocale]: { ...prev[activeLocale], src: url },
        }));
      } catch (error) {
        console.error('Upload failed:', error);
        // Optionally, handle the error in the UI
      }
    }
  };

  const handleMediaLibrary = async () => {
    if (mediaLibraryCallback) {
      try {
        const url = await mediaLibraryCallback({ blockId: block.id });
        const currentType = localeContent[activeLocale]?.type || 'image';
        
        if (currentType === 'gallery') {
          setLocaleContent((prev) => ({
            ...prev,
            [activeLocale]: { 
              ...prev[activeLocale], 
              gallerySrc: [...(prev[activeLocale]?.gallerySrc || []), url] 
            },
          }));
        } else {
          setLocaleContent((prev) => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], src: url },
          }));
        }
      } catch (error) {
        console.error('Media library selection failed:', error);
      }
    }
  };

  const handleRemoveGalleryImage = (index: number) => {
    setLocaleContent((prev) => ({
      ...prev,
      [activeLocale]: {
        ...prev[activeLocale],
        gallerySrc: (prev[activeLocale]?.gallerySrc || []).filter((_, i) => i !== index),
      },
    }));
  };

  const handleMoveGalleryImageLeft = (index: number) => {
    if (index === 0) return;
    setLocaleContent((prev) => {
      const gallerySrc = [...(prev[activeLocale]?.gallerySrc || [])];
      [gallerySrc[index - 1], gallerySrc[index]] = [gallerySrc[index], gallerySrc[index - 1]];
      return {
        ...prev,
        [activeLocale]: {
          ...prev[activeLocale],
          gallerySrc,
        },
      };
    });
  };

  const handleMoveGalleryImageRight = (index: number) => {
    const gallerySrcLength = localeContent[activeLocale]?.gallerySrc?.length || 0;
    if (index === gallerySrcLength - 1) return;
    setLocaleContent((prev) => {
      const gallerySrc = [...(prev[activeLocale]?.gallerySrc || [])];
      [gallerySrc[index], gallerySrc[index + 1]] = [gallerySrc[index + 1], gallerySrc[index]];
      return {
        ...prev,
        [activeLocale]: {
          ...prev[activeLocale],
          gallerySrc,
        },
      };
    });
  };

  const handleAddGalleryImage = async () => {
    if (newMediaCallback) {
      try {
        const url = await newMediaCallback({ blockId: block.id });
        setLocaleContent((prev) => ({
          ...prev,
          [activeLocale]: { 
            ...prev[activeLocale], 
            gallerySrc: [...(prev[activeLocale]?.gallerySrc || []), url] 
          },
        }));
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  };

  return (
    <TemplateSettingsDialog
      title="MediaBlock Settings"
      onClose={onClose}
      onSave={handleSave}
    >
      <LocaleFieldWrapper>
        <label className="block mb-2">
          Default Type ({activeLocale.toUpperCase()}):
          <select
            className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
            value={localeContent[activeLocale]?.type || 'image'}
            onChange={handleTypeChange}
          >
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="gallery">Gallery</option>
          </select>
        </label>
      </LocaleFieldWrapper>

      {(localeContent?.[activeLocale]?.type || 'image') !== 'gallery' && (
        <LocaleFieldWrapper>
          <div className="flex items-end space-x-4">
            <label className="block flex-1">
              {(localeContent?.[activeLocale]?.type || 'image') === 'video' ? 'Default YouTube URL' : 'Default Source'} ({activeLocale.toUpperCase()}):
              <input
                type="text"
                className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
                value={localeContent[activeLocale]?.src || ''}
                onChange={handleSrcChange}
                placeholder={(localeContent?.[activeLocale]?.type || 'image') === 'video' ? 'https://www.youtube.com/watch?v=...' : ''}
              />
            </label>
            {newMediaCallback && ((localeContent?.[activeLocale]?.type || 'image') === 'image') && (
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                onClick={handleNewMediaClick}
              >
                New Media
              </button>
            )}
            {mediaLibraryCallback && ((localeContent?.[activeLocale]?.type || 'image') === 'image') && (
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                onClick={handleMediaLibrary}
              >
                Select from Media Library
              </button>
            )}
          </div>
        </LocaleFieldWrapper>
      )}

      {(localeContent?.[activeLocale]?.type || 'image') === 'gallery' && (
        <LocaleFieldWrapper>
          <div className="block mb-4">
            <label className="block mb-2 font-semibold">
              Gallery Images ({activeLocale.toUpperCase()}):
            </label>
            <div className="flex gap-2 mb-3">
              {newMediaCallback && (
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  onClick={handleAddGalleryImage}
                >
                  Upload Image
                </button>
              )}
              {mediaLibraryCallback && (
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  onClick={handleMediaLibrary}
                >
                  Select from Media Library
                </button>
              )}
            </div>
            <div className="flex gap-3 overflow-x-auto p-2 bg-gray-50 rounded border border-gray-200">
              {(localeContent[activeLocale]?.gallerySrc || []).length === 0 ? (
                <div className="text-gray-400 text-sm py-4">No images added yet</div>
              ) : (
                (localeContent[activeLocale]?.gallerySrc || []).map((imgSrc, index) => (
                  <div
                    key={index}
                    className="relative flex-shrink-0 w-[120px] rounded border border-gray-300"
                  >
                    <div className="w-[120px] h-[120px] overflow-hidden">
                      <img
                        src={imgSrc}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveGalleryImage(index)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full border-0 bg-red-600/80 text-white cursor-pointer text-sm flex items-center justify-center p-0 leading-none hover:bg-red-700/90"
                      title="Remove image"
                    >
                      ×
                    </button>
                    <div className="flex gap-1 p-1 bg-white">
                      <button
                        onClick={() => handleMoveGalleryImageLeft(index)}
                        disabled={index === 0}
                        className="flex-1 px-2 py-1 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                        title="Move left"
                      >
                        ← Left
                      </button>
                      <button
                        onClick={() => handleMoveGalleryImageRight(index)}
                        disabled={index === (localeContent[activeLocale]?.gallerySrc || []).length - 1}
                        className="flex-1 px-2 py-1 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                        title="Move right"
                      >
                        Right →
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </LocaleFieldWrapper>
      )}
      <LocaleFieldWrapper>
        <label className="block mb-2 mt-2">
          Default Alt Text ({activeLocale.toUpperCase()}):
          <input
            type="text"
            className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
            value={localeContent[activeLocale]?.altText || ''}
            onChange={handleAltTextChange}
          />
        </label>
      </LocaleFieldWrapper>
    </TemplateSettingsDialog>
  );
};

export default MediaBlockTemplateSettingsDialog;